<template>
  <g
    v-if="lines.length > 0"
    ref="el"
    class="net__connection net__connection_normal is-flow"
    transform="translate(0,0)"
    :class="[
      {
        'is-selected': selected,
        'is-highlighted': highlighted,
        'is-faded': isFaded,
        'is-incoming': incoming,
        'is-outgoing': outgoing,
        'is-connecting': connecting,
        'is-zero': isZero,
        'is-unclickable': !clickable
      }
    ]"
    @contextmenu.prevent="onContextMenu"
  >
    <template v-if="selected || incoming || outgoing || endpointsOnly == false">
      <!--
        The linebounds path is used to catch any clicks on the path. Since
        the main path might have holes in it (because of stroke-dasharray),
        we need a continous line to catch the clicks in these holes.
      -->
      <path
        class="net__connection__linebounds"
        :d="description"
        @click.exact="select"
        @click.shift="addToSelection"
        @pointerdown="markAsHandled"
      />

      <!--
        stroke-dasharray below HAS to be written in the ternary way inside
        the style attribute. I tried moving it to a computed property, but
        the path wouldn't update correctly when changing from zero to
        standard.
      -->
      <path
        class="net__connection__line"
        :d="description"
        :style="{
          'stroke-dasharray': isZero ? '4' : `1 0`
        }"
        @click.exact="select"
        @click.shift="addToSelection"
        @pointerdown="markAsHandled"
      />
      <path
        class="net__connection__line net__connection__line--fill"
        :d="description"
        :style="{
          'stroke-dasharray': isZero ? '4' : `1 0`
        }"
      />

      <g v-if="(branchType ?? flowBranch?.branchType) == 'sink'" :transform="`translate(${end?.x}, ${end?.y})`">
        <polygon
          :points="arrowheadPoints"
          class="net__connection__arrow"
          @click.exact="select"
          @click.shift="addToSelection"
          @pointerdown="markAsHandled"
        />
      </g>
      <g v-if="flowSinks.length == 0 && (branchType ?? flowBranch?.branchType) == 'source'" :transform="`translate(${sourceEndCoordinate?.x}, ${sourceEndCoordinate?.y})`">
        <polygon
          :points="arrowheadPoints"
          class="net__connection__arrow"
          @click.exact="select"
          @click.shift="addToSelection"
          @pointerdown="markAsHandled"
        />
      </g>
    </template>
    <template v-else>
      <!-- OUT -->
      <path
        class="net__connection__linebounds"
        :d="lineOutDescription"
        @click.exact="select"
        @click.shift="addToSelection"
        @pointerdown="markAsHandled"
      />
      <path
        class="net__connection__line"
        :d="lineOutDescription"
        :style="{
          'stroke-dasharray': isZero ? '4' : `1 0`
        }"
        @click.exact="select"
        @click.shift="addToSelection"
        @pointerdown="markAsHandled"
      />
      <path
        class="net__connection__line net__connection__line--fill"
        :d="lineOutDescription"
        :style="{
          'stroke-dasharray': isZero ? '4' : `1 0`
        }"
      />

      <!-- IN -->
      <path
        class="net__connection__linebounds"
        :d="lineInDescription"
        @click.exact="select"
        @click.shift="addToSelection"
        @pointerdown="markAsHandled"
      />
      <path
        class="net__connection__line"
        :d="lineInDescription"
        :style="{
          'stroke-dasharray': isZero ? '4' : `1 0`
        }"
        @click.exact="select"
        @click.shift="addToSelection"
        @pointerdown="markAsHandled"
      />
      <path
        class="net__connection__line net__connection__line--fill"
        :d="lineInDescription"
        :style="{
          'stroke-dasharray': isZero ? '4' : `1 0`
        }"
      />

      <g v-if="(branchType || flowBranch?.branchType == 'sink')" :transform="`translate(${end?.x}, ${end?.y})`">
        <polygon
          :points="arrowheadPoints"
          class="net__connection__arrow"
          @click.exact="select"
          @click.shift="addToSelection"
          @pointerdown="markAsHandled"
        />
      </g>
    </template>

    <g class="net-flow__value" :transform="`translate(${nearestPoint.x}, ${ nearestPoint.y })`">
      <rect class="net-flow__value-bg" :x="flowValueXOffset" :y="-5" height="10" :width="formattedValueWidth"></rect>
      <text class="net-flow__value-text" y="5" :text-anchor="nearestPoint.direction == 'right' ? 'end' : (nearestPoint.direction == 'left' ? 'start' : 'middle')">
        {{ formattedValue }}
      </text>
    </g>
  </g>
</template>

<script setup lang="ts">
/**
 * This is the line that is used to show connections between elements in the net.
 */
import { computed, inject, ref } from "vue";
import { useSVGTextWidthCache } from "@core/composables/useSVGTextWidthCache";

import { useFlowBranchStore } from "./flowBranches.store";
import { useFlowStore } from "./flows.store";
import { useSceneTree } from "../scene/sceneTree";
import { formatNumber } from "@/helpers/utility/Formatting";
import { keyElementValues } from "@editor/net/netCanvasInjectionKeys";

import Vector from "@editor/items/relation/Vector.js";
import QuadCurve from "@editor/items/relation/relation_distribution/QuadCurve";
import Line from "@editor/items/relation/relation_distribution/Line";

import type { Handleable } from "@core/types/Events";
import type { FlowItem } from "./FlowItem";
import type { FlowBranchItem } from "./FlowBranchItem";

defineOptions({
  name: "NetRelation"
});

interface Props {
  flowBranchId: number;
  lines?: (Line | QuadCurve)[];
  selected?: boolean;
  connecting?: boolean;
  highlighted?: boolean;
  fade?: boolean;
  incoming?: boolean;
  outgoing?: boolean;
  endpointsOnly?: boolean;
  value?: number;
  branchType?: string;
  clickable?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  lines: () => [],
  selected: false,
  connecting: false,
  highlighted: false,
  fade: false,
  incoming: false,
  outgoing: false,
  endpointsOnly: false,
  clickable: true
});

const emit = defineEmits<{
  (e: "select", flow: FlowItem): void;
  (e: "add-select", flow: FlowItem): void;
  (e: "contextmenu", event: MouseEvent, flow: FlowBranchItem | null): void;
}>();

const el = ref<SVGElement | null>(null);
  
const svgOwnerElement = computed(() => el.value?.ownerSVGElement ?? undefined);

const { measureTextWidthCached } = useSVGTextWidthCache(svgOwnerElement);
const sceneTree = useSceneTree();
const flowBranchStore = useFlowBranchStore();
const flowStore = useFlowStore();

const flowBranch = computed(() => {
  if (props.flowBranchId == null) return null;
  return flowBranchStore.items[props.flowBranchId];
});

const flow = computed(() => {
  if (!flowBranch.value) return null;
  return flowStore.items[flowBranch.value?.flowId];
});

const flowSinks = computed(() => {
  if (!flow.value) return [];
  const branches = flowBranchStore.byFlow[flow.value.id] ?? [];
  return branches.filter((b) => b.branchType == "sink");
});

// ===================
// RELATION STATUS

const isFaded = computed(() => {
  if (
    props.selected ||
    props.highlighted ||
    props.incoming ||
    props.outgoing ||
    props.connecting
  )
    return false;
  return props.fade;
});

const isZero = computed(() => {
  if (!flow.value) return true;
  const expression = flowStore.expressions[flow.value.id].trim();
  return expression == "0" || expression == "";
});

const injectedElementValues = inject(keyElementValues);
const elementValues = computed(() => injectedElementValues?.value || {});

const formattedValue = computed(() => {
  if (!flowBranch.value) return "";
  return formatNumber(elementValues.value[flowBranch.value.uid]?.value, {
    maximumFractionDigits: 1,
    signDisplay: "always"
  });
});

const sceneFontSize = computed(() => {
  return sceneTree.activeScene.fontSize;
});
const formattedValueWidth = computed(() => {
  if (!flowBranch.value) return 0;
  return measureTextWidthCached(formattedValue.value, sceneFontSize.value * 0.8);
});

const flowValueXOffset = computed(() => {
  if (nearestPoint.value.direction == "right") return -formattedValueWidth.value;
  if (nearestPoint.value.direction == "left") return 0;
  return -formattedValueWidth.value / 2;
});

// ===================
// SVG properties

const end = computed(() => {
  if (props.lines.length === 0) return null;
  return props.lines[props.lines.length - 1].end;
});

const sourceEndCoordinate = computed(() => {
  if (end.value == null) return null;
  let offsetedEnd = { ...end.value };
  const lines = props.lines;
  const lastLine = lines[lines.length - 1];
  if (lastLine.direction === "up") {
    offsetedEnd.y += 5;
  } else if (lastLine.direction === "down") {
    offsetedEnd.y -= 5;
  } else if (lastLine.direction === "left") {
    offsetedEnd.x += 5;
  } else if (lastLine.direction === "right") {
    offsetedEnd.x -= 5;
  }
  return offsetedEnd;
});

/**
 * SVG Path description of the total path.
 * @see https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/d#path_commands
 */
const description = computed(() => {
  if (props.lines.length === 0) return "";

  const isSink = flowBranch.value?.branchType == "sink";
  const hasArrow = isSink || flowSinks.value.length == 0;

  let lines = props.lines;
  let description = `M${lines[0].start.x} ${lines[0].start.y}`;
  let useStartToo = false;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (useStartToo) {
      description += `L${line.start.x} ${line.start.y}`;
      useStartToo = false;
    }
    if (QuadCurve.getLength(line) === 0) useStartToo = true;
    else {
      if (line.type === "curve") {
        const curve = line as QuadCurve;
        description += `Q${curve.bezier.x} ${curve.bezier.y}, ${curve.end.x} ${curve.end.y}`;
      } else {
        // don't move the last line to the end
        // because the arrowhead will be drawn there
        if (hasArrow && i == lines.length - 1) {
          const END_MARGIN = isSink ? 7 : 15;
          switch (line.direction) {
            case "down":
              description += `L${line.end.x} ${line.end.y - END_MARGIN}`;
              break;

            case "up":
              description += `L${line.end.x} ${line.end.y + END_MARGIN}`;
              break;

            case "left":
              description += `L${line.end.x + END_MARGIN} ${line.end.y}`;
              break;

            case "right":
              description += `L${line.end.x - END_MARGIN} ${line.end.y}`;
              break;
          }
        } else {
          description += `L${line.end.x} ${line.end.y}`;
        }
      }
    }
  }
  return description;
});

/**
 * SVG Path description for the arrow at the end of the line.
 * @see https://developer.mozilla.org/en-US/docs/Web/SVG/Attribute/d#path_commands
 */
const arrowheadPoints = computed(() => {
  if (end.value == null) return "";
  const lastPoint = props.lines[props.lines.length - 1].start;
  const cursor = new Vector(0, 0);
  let pointString = `${cursor.x},${cursor.y} `;
  const arrowWidth = 19;
  const movement1 = new Vector(-15, 0);
  const movement2 = new Vector(0, arrowWidth / 2);
  const movement3 = new Vector(0, -arrowWidth);
  let degree = Math.PI;
  const bezier = new Vector(
    lastPoint.x - end.value.x,
    lastPoint.y - end.value.y
  );
  degree += bezier.toAngles();
  movement1.turn(degree);
  movement2.turn(degree);
  movement3.turn(degree);
  cursor.add(movement1);
  cursor.add(movement2);
  pointString += `${cursor.x},${cursor.y} `;
  cursor.add(movement3);
  pointString += `${cursor.x},${cursor.y}`;
  return pointString;
});

const ENDPOINT_LENGTH = 20;

const lineInStartPoint = computed(() => {
  const start = props.lines[props.lines.length - 1].start;
  const end = props.lines[props.lines.length - 1].end;
  if (start.x == end.x) {
    return {
      x: start.x,
      y: clamp(start.y, end.y - ENDPOINT_LENGTH, end.y + ENDPOINT_LENGTH),
      direction: start.y < end.y ? "down" : "up"
    };
  }
  if (start.y == end.y) {
    return {
      x: clamp(start.x, end.x - ENDPOINT_LENGTH, end.x + ENDPOINT_LENGTH),
      y: start.y,
      direction: start.x < end.x ? "right" : "left"
    };
  }
  return {
    x: start.x,
    y: start.y,
    direction: "right"
  };
});
const lineInDescription = computed(() => {
  const startPoint = lineInStartPoint.value;
  const end = props.lines[props.lines.length - 1].end;
  
  // cut off the last part of the line
  // to make space for the arrowhead
  let endPoint = { ...end };
  if (startPoint.x == end.x) {
    if (endPoint.y > startPoint.y) {
      endPoint.y -= 8;
    } else {
      endPoint.y += 8;
    }
  }
  if (startPoint.y == end.y) {
    if (endPoint.x > startPoint.x) {
      endPoint.x -= 8;
    } else {
      endPoint.x += 8;
    }
  }
  
  return `M${startPoint.x} ${startPoint.y}L${endPoint.x} ${endPoint.y}`;
});

const lineOutEndPoint = computed(() => {
  const start = props.lines[0].start;
  const end = props.lines[0].end;
  if (start.x == end.x) {
    return {
      x: end.x,
      y: clamp(end.y, start.y - ENDPOINT_LENGTH, start.y + ENDPOINT_LENGTH),
      direction: end.y < start.y ? "down" : "up"
    };
  }
  if (start.y == end.y) {
    return {
      x: clamp(end.x, start.x - ENDPOINT_LENGTH, start.x + ENDPOINT_LENGTH),
      y: end.y,
      direction: end.x < start.x ? "right" : "left"
    };
  }
  return {
    x: end.x,
    y: end.y,
    direction: "right"
  };
});
const lineOutDescription = computed(() => {
  const start = props.lines[0].start;
  return `M${start.x} ${start.y}L${lineOutEndPoint.value.x} ${lineOutEndPoint.value.y}`;
});

const nearestPoint = computed(() => {
  if (flowBranch.value?.branchType == "sink") return lineInStartPoint.value;
  return lineOutEndPoint.value;
});

function clamp(value: number, min: number, max: number) {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}

// ===================
// Methods

function markAsHandled(mouseEvent: MouseEvent & Handleable) {
  mouseEvent.preventDefault();
  mouseEvent.hasBeenHandled = true;
}

/**
 * Emit event to select this relation.
 */
function select(mouseEvent: MouseEvent & Handleable) {
  mouseEvent.preventDefault();
  mouseEvent.hasBeenHandled = true;
  if (!flow.value) return;

  emit("select", flow.value);
}

/**
 * Emit event to add relation to selection.
 */
function addToSelection(mouseEvent: MouseEvent & Handleable) {
  mouseEvent.preventDefault();
  mouseEvent.hasBeenHandled = true;
  if (flow.value) {
    emit("add-select", flow.value);
  }
}

function onContextMenu(event: MouseEvent) {
  emit("contextmenu", event, flowBranch.value);
}
</script>

<style lang="scss">
.net__connection {
  pointer-events: auto;

  --relation-color: var(--net-color-relation-standard);

  &.is-inverse {
    --relation-color: var(--net-color-relation-inverse);
  }
  &.is-incoming {
    --relation-color: var(--net-color-relation-incoming);
  }
  &.is-incoming.is-inverse {
    --relation-color: var(--net-color-relation-inverse-incoming);
  }
  &.is-outgoing {
    --relation-color: var(--net-color-relation-outgoing);
  }
  &.is-outgoing.is-inverse {
    --relation-color: var(--net-color-relation-inverse-outgoing);
  }
  &.is-connecting {
    --relation-color: var(--net-color-relation-connecting);
  }
  &.is-connecting.is-inverse {
    --relation-color: var(--net-color-relation-inverse-connecting);
  }
  &.is-selected {
    --relation-color: var(--net-color-relation-active);
  }
  &.is-selected.is-inverse {
    --relation-color: var(--net-color-relation-inverse-active);
  }
  &.is-highlighted {
    --relation-color: var(--net-color-relation-highlighted);
  }
  &.is-highlighted.is-inverse {
    --relation-color: var(--net-color-relation-inverse-highlighted);
  }
  &.is-faded {
    --relation-color: var(--net-color-relation-faded);
  }

  &__linebounds {
    stroke-width: 10px;
    stroke: transparent;
    fill: none;
  }
  &__line {
    stroke: var(--relation-color);
    fill: none;
    stroke-linejoin: round;
    stroke-linecap: round;

    --stroke-width: 1px;
    --extra-stroke-width: 0px;

    stroke-width: var(--stroke-width);

    &--fill {
      pointer-events: none;
      stroke: rgba(255, 255, 255, 0.8);
    }
  }
  .is-zero &__line,
  .is-flow &__line {
    stroke-linecap: butt;
  }
  .is-flow &__line {
    --extra-stroke-width: 3px;
  }

  &_weak &__line {
    --stroke-width: 2px;
  }
  &_weak.is-flow &__line {
    --stroke-width: 4px;
  }
  &_normal &__line {
    --stroke-width: 4px;
  }
  &_normal.is-flow &__line {
    --stroke-width: 6px;
  }
  &_strong &__line {
    --stroke-width: 5px;
  }
  &_strong.is-flow &__line {
    --stroke-width: 7px;
  }

  &_weak &__line--fill {
    stroke-width: 2px !important;
  }
  &_normal &__line--fill {
    stroke-width: 3px !important;
  }
  &_strong &__line--fill {
    stroke-width: 4px !important;
  }

  .is-selected &__line,
  .is-outgoing &__line,
  .is-incoming &__line {
    stroke-width: calc(var(--stroke-width) + 1px);
  }

  &__arrow {
    fill: var(--relation-color);
  }
  .is-flow &__arrow {
    fill: var(--relation-color);
  }

  &_weak &__arrow {
    transform: scale(0.5);
  }
  &_weak.is-flow &__arrow {
    transform: scale(0.5);
  }
  &_normal &__arrow {
    transform: scale(1);
  }
  &_normal.is-flow &__arrow {
    transform: scale(0.75);
  }
  &_strong &__arrow {
    transform: scale(1.25);
  }
  &_strong.is-flow &__arrow {
    transform: scale(1);
  }

  &__inversemarker {
    &__circle {
      fill: var(--relation-color);
      transform: scale(0.6);
      transition: transform 125ms;

      .is-selected & {
        transform: scale(1);
      }
      .is-faded & {
        fill: var(--net-color-relation-faded);
      }
    }
    &__icon {
      color: var(--color-action-contrast);
    }
  }

  &.is-unclickable {
    pointer-events: none;
  }

  &:hover {
    filter: drop-shadow(0 3px 3px rgba(0, 0, 0, 0.1));
  }
}
.net__bezierhandle {
  .is-selected & {
    z-index: 10;
  }
}
.net-flow {
  &__value {
    pointer-events: none;
  }
  &__value-bg {
    fill: var(--color-background-deep);
    opacity: 0.6;
  }
  &__value-text {
    font-size: 0.8em;
    fill: var(--color-text-medium);
  }
}
</style>
